import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Users, Package, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Wholesale() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    quantity: 10,
    timeline: "",
    message: "",
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) {
      toast.error("Please fill in name, email, and organization.");
      return;
    }
    if (form.quantity < 10) {
      toast.error("Bulk inquiries are for 10+ units. For smaller orders use checkout.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/wholesale-inquiry`, {
        ...form,
        quantity: parseInt(form.quantity, 10),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="wholesale-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left: Pitch */}
          <div className="lg:col-span-2">
            <p className="eyebrow mb-4">Bulk &amp; Wholesale</p>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05]">
              Outfit your entire
              <br />
              <span className="text-[#E8A851]">production team.</span>
            </h1>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Ordering for a multisite church, touring band, school, or AV
              integrator? We offer volume pricing on orders of 10 or more
              Aura Sleeves — and we'll work with your purchasing process.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Building2 className="w-5 h-5 text-[#E8A851] mt-1 shrink-0" />
                <div>
                  <p className="font-display text-lg font-medium">Multisite friendly</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    Single invoice, split shipping to each campus.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Package className="w-5 h-5 text-[#E8A851] mt-1 shrink-0" />
                <div>
                  <p className="font-display text-lg font-medium">Volume pricing</p>
                  <ul className="text-sm text-zinc-400 leading-relaxed mt-2 space-y-1.5">
                    <li className="flex items-center gap-2 flex-wrap">
                      <span>10–24 units · <span className="text-white">$51</span> each</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#E8A851]/90 border border-[#E8A851]/30 rounded-full px-2 py-0.5">Save 14%</span>
                    </li>
                    <li className="flex items-center gap-2 flex-wrap">
                      <span>25–49 units · <span className="text-white">$50</span> each</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#E8A851]/90 border border-[#E8A851]/30 rounded-full px-2 py-0.5">Save 15%</span>
                    </li>
                    <li className="flex items-center gap-2 flex-wrap">
                      <span>50+ units · <span className="text-white">$49</span> each</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#E8A851]/90 border border-[#E8A851]/30 rounded-full px-2 py-0.5">Save 17%</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Users className="w-5 h-5 text-[#E8A851] mt-1 shrink-0" />
                <div>
                  <p className="font-display text-lg font-medium">PO &amp; net terms</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    We can work with church business offices and AV integrators.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-zinc-500 mt-12 leading-relaxed">
              Need fewer than 10? Just{" "}
              <Link to="/product" className="text-white underline">
                order on the product page
              </Link>
              .
            </p>
          </div>

          {/* Right: Form / Success */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div
                data-testid="wholesale-success-state"
                className="border border-[#E8A851]/40 bg-gradient-to-br from-[#E8A851]/10 to-transparent p-10"
              >
                <CheckCircle2 className="w-12 h-12 text-[#E8A851] mb-6" />
                <p className="eyebrow mb-3">Inquiry received</p>
                <h2 className="font-display text-3xl font-medium tracking-tight">
                  Thanks, {form.name.split(" ")[0]}.
                </h2>
                <p className="mt-4 text-zinc-300 leading-relaxed">
                  We'll get back to {form.email} within 1 business day with a
                  custom quote for {form.quantity} Aura Sleeves for{" "}
                  <span className="text-white">{form.organization}</span>.
                </p>
                <Link
                  to="/"
                  data-testid="wholesale-back-home"
                  className="btn-secondary mt-10 inline-flex"
                >
                  Back to home <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <form
                data-testid="wholesale-form"
                onSubmit={handleSubmit}
                className="border border-white/10 bg-white/[0.02] p-8 md:p-10 space-y-7"
              >
                <p className="eyebrow">Request a quote</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <Field label="Your name" required>
                    <input
                      data-testid="wholesale-name-input"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Pat Johnson"
                      required
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="wholesale-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="pat@yourchurch.org"
                      required
                    />
                  </Field>
                  <Field label="Organization" required>
                    <input
                      data-testid="wholesale-org-input"
                      type="text"
                      value={form.organization}
                      onChange={update("organization")}
                      placeholder="Hillview Church"
                      required
                    />
                  </Field>
                  <Field label="Your role">
                    <input
                      data-testid="wholesale-role-input"
                      type="text"
                      value={form.role}
                      onChange={update("role")}
                      placeholder="Tech Director"
                    />
                  </Field>
                  <Field label="Quantity (10+)" required>
                    <input
                      data-testid="wholesale-quantity-input"
                      type="number"
                      min="10"
                      value={form.quantity}
                      onChange={update("quantity")}
                      required
                    />
                  </Field>
                  <Field label="Timeline">
                    <input
                      data-testid="wholesale-timeline-input"
                      type="text"
                      value={form.timeline}
                      onChange={update("timeline")}
                      placeholder="Need by Easter"
                    />
                  </Field>
                </div>

                <Field label="Anything else?">
                  <textarea
                    data-testid="wholesale-message-input"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your stage setup, # of campuses, special requirements…"
                  />
                </Field>

                <button
                  data-testid="wholesale-submit-button"
                  disabled={loading}
                  type="submit"
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Request quote <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-zinc-500 leading-relaxed">
                  We'll respond within 1 business day. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">
        {label} {required && <span className="text-[#E8A851]">*</span>}
      </span>
      <div className="[&_input]:w-full [&_textarea]:w-full [&_input]:bg-transparent [&_textarea]:bg-transparent [&_input]:border-b [&_textarea]:border [&_input]:border-white/20 [&_textarea]:border-white/20 [&_input]:focus:border-white [&_textarea]:focus:border-white [&_input]:text-white [&_textarea]:text-white [&_input]:px-0 [&_textarea]:px-4 [&_input]:py-3 [&_textarea]:py-3 [&_input]:placeholder:text-white/30 [&_textarea]:placeholder:text-white/30 [&_input]:focus:outline-none [&_textarea]:focus:outline-none [&_textarea]:leading-relaxed">
        {children}
      </div>
    </label>
  );
}
